import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MAIN_GOAL_LABELS } from '../constants';
import { replaceExerciseIdentityInBlocks } from '../pages/Training/utils/replaceTrainingExercise';
import {
  extractExerciseFromTree,
  flattenExerciseCluster,
  reorderExercisesInSameCluster,
} from '../pages/Training/utils/reorderTrainingExercises';
import { moveBlockByIds, moveBlockToLabel, relabelTrainingBlocks } from '../pages/Training/utils/reorderTrainingBlocks';
import {
  resolveTrainingExerciseMuscleGroups,
} from '../pages/Training/utils/trainingMetrics';
import { ExerciseService } from '../services/ExerciseService';
import { TrainingService } from '../services';
import type { ExerciseItem, ExerciseMethodLookup, ExerciseRecord } from '../types/exercise';
import {
  MAIN_GOAL_OPTIONS,
  TRAINING_LOCATION_OPTIONS,
  type MainGoal,
  type TrainingLevel,
  type TrainingLocation,
} from '../types/registration';
import type {
  TrainingBlock,
  TrainingBlockExercise,
  TrainingExerciseGroupingType,
  TrainingCategory,
  TrainingExerciseExecution,
  TrainingExerciseExecutionDetail,
  TrainingExerciseMethodConfig,
  TrainingFormErrors,
  TrainingFormValues,
  TrainingItem,
  TrainingMuscleVolume,
  TrainingRecord,
  TrainingSummaryMetric,
  TrainingSummaryResponse,
} from '../types/training';
import {
  TRAINING_CATEGORY_LABELS,
  TRAINING_CATEGORY_OPTIONS,
  WORKOUT_TRAINING_LEVEL_OPTIONS,
  isTabataSupportedTrainingGroupingType,
} from '../types/training';
import {
  getCanonicalTrainingExecutionMethodValue,
  getTrainingExecutionMethodDefinition,
  getTrainingExecutionMethodDisplayLabel,
  isDropFamilyTrainingExecutionMethod,
  isPyramidTrainingExecutionMethod,
  isRestPauseTrainingExecutionMethod,
  isTabataTrainingExecutionMethod,
  normalizeTrainingExecutionMethodKey,
  usesPrescribedLoadPercentage,
} from '../utils/trainingExecutionMethods';
import { getPyramidLoadPercentageValue } from '../utils/pyramidLoadPercentage';
import {
  DEFAULT_REST_SECONDS,
  buildExecutionSpeedFields,
  emptyExecutionSpeedFields,
  isIsometryExecutionSpeed,
} from '../utils/executionSpeed';
import { DEFAULT_REPETITION_SCOPE, normalizeRepetitionScope } from '../utils/repetitionScope';
import {
  MAX_WORKOUT_LEVELS,
  WORKOUT_LEVELS_GAP_ERROR,
  WORKOUT_LEVELS_MAX_ERROR,
  areWorkoutLevelsContiguous,
  expandLegacyWorkoutLevels,
  resolvePrimaryWorkoutLevel,
} from '../utils/workoutTrainingLevels';
import useExercises from './useExercises';
import { queryFilter, useQueryFilters } from './useQueryFilters';

type TrainingSummaryRequestPayload = Parameters<typeof TrainingService.getTrainingSummary>[0];

interface SelectOption {
  value: string;
  label: string;
}

interface ExecutionMethodOption {
  id?: number;
  value: string;
  label: string;
  group?: string;
  color?: string | null;
  textColor?: string | null;
}

interface ActiveExerciseConfig {
  blockId: string;
  exerciseId: string;
}

type ExerciseDropMode = 'insert' | 'insert-after' | 'conjugate';

interface UseTrainingBuilderResult {
  mode: 'create' | 'edit';
  formValues: TrainingFormValues;
  errors: TrainingFormErrors;
  blocks: TrainingBlock[];
  submitError: string;
  isSaving: boolean;
  isLoadingTraining: boolean;
  isMissingTraining: boolean;
  catalogExercises: ExerciseItem[];
  catalogLoading: boolean;
  catalogSearchTerm: string;
  catalogGroups: string[];
  catalogGroupOptions: SelectOption[];
  hasActiveCatalogFilters: boolean;
  selectedExercise: ExerciseItem | null;
  isSummaryLoading: boolean;
  summaryError: string;
  summaryMetrics: TrainingSummaryMetric[];
  muscleVolumes: TrainingMuscleVolume[];
  blockSummaryMetricsById: Record<string, TrainingSummaryMetric[]>;
  activeExerciseConfig: ActiveExerciseConfig | null;
  executionMethodOptions: ExecutionMethodOption[];
  categoryOptions: SelectOption[];
  levelOptions: SelectOption[];
  objectiveOptions: SelectOption[];
  monthOptions: SelectOption[];
  locationOptions: SelectOption[];
  frequencyOptions: SelectOption[];
  handleFieldChange: (field: keyof TrainingFormValues, value: string | string[]) => void;
  handleCatalogSearchChange: (value: string) => void;
  handleCatalogGroupsChange: (values: string[]) => void;
  handleClearCatalogFilters: () => void;
  handleSelectExercise: (exercise: ExerciseItem) => void;
  handleAddExerciseToBlock: (blockId: string) => void;
  handleDropCatalogExerciseToBlock: (blockId: string, exercise: ExerciseItem, targetExerciseId?: string, dropMode?: ExerciseDropMode) => void;
  handleOpenExerciseConfig: (blockId: string, exerciseId: string) => Promise<void>;
  handleCloseExerciseConfig: () => void;
  handleUpdateExerciseExecution: (blockId: string, exerciseId: string, execution: TrainingExerciseExecution) => void;
  handleRemoveExerciseFromBlock: (blockId: string, exerciseId: string) => void;
  handleReplaceExercise: (
    blockId: string,
    itemId: string,
    nextExercise: ExerciseItem,
    replaceInEntirePlan: boolean,
  ) => void;
  handleMoveExerciseLeft: (blockId: string, exerciseId: string) => void;
  handleMoveExerciseRight: (blockId: string, exerciseId: string) => void;
  handleMoveExerciseByDrag: (sourceBlockId: string, exerciseId: string, targetBlockId: string, targetExerciseId?: string, dropMode?: ExerciseDropMode) => void;
  handleMoveBlock: (blockId: string, direction: 'up' | 'down') => void;
  handleMoveBlockByDrag: (sourceBlockId: string, targetBlockId: string) => void;
  handleMoveBlockToLabel: (blockId: string, targetLabel: string) => void;
  handleRemoveBlock: (blockId: string) => void;
  handleAddBlock: () => void;
  handleSave: () => Promise<TrainingRecord | null>;
}

export interface TrainingBuilderStorage {
  getTrainingById: (trainingId: string) => Promise<TrainingRecord | null>;
  saveTraining: (training: TrainingRecord) => Promise<TrainingRecord>;
}

const CATALOG_PAGE_SIZE = 1000;
const TRAINING_CATALOG_QUERY_SCHEMA = {
  catalogSearch: queryFilter.string(),
  catalogGroups: queryFilter.array(),
};

// Generate training block labels: A-Z, then AA-AZ, BA-BZ, ..., ZA-ZZ
// Total: 26 + (26 * 26) = 702 labels, max label is "ZZ" and stored as varchar(2)
const generateBlockLabels = (): string[] => {
  const labels: string[] = [];

  // Single letters: A-Z
  for (let i = 0; i < 26; i++) {
    labels.push(String.fromCharCode(65 + i));
  }

  // Double letters: AA-ZZ
  for (let first = 0; first < 26; first++) {
    for (let second = 0; second < 26; second++) {
      labels.push(String.fromCharCode(65 + first) + String.fromCharCode(65 + second));
    }
  }

  return labels;
};

const BLOCK_LABELS = generateBlockLabels();
const MAX_BLOCK_LABEL_INDEX = BLOCK_LABELS.length - 1; // 701, for label "ZZ"

const DEFAULT_SUMMARY_TIME = '00min00s';
const DEFAULT_SUMMARY_VALUE = '—';
const EMPTY_SUMMARY_VALUES: SummaryMetricValues = {
  totalVolume: '0',
  totalTime: DEFAULT_SUMMARY_TIME,
  loadPercentage: '0%',
  loadIntensity: '- -',
  effortPerception: '- -',
};

const CATEGORY_OPTIONS: SelectOption[] = TRAINING_CATEGORY_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const LEVEL_OPTIONS: SelectOption[] = WORKOUT_TRAINING_LEVEL_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const OBJECTIVE_OPTIONS: SelectOption[] = MAIN_GOAL_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const MONTH_OPTIONS: SelectOption[] = Array.from({ length: 24 }, (_, index) => ({
  value: String(index + 1),
  label: `Mês ${index + 1}`,
}));

const LEGACY_MONTH_VALUE_MAP: Record<string, string> = {
  Janeiro: '1',
  Fevereiro: '2',
  Marco: '3',
  Março: '3',
  Abril: '4',
  Maio: '5',
  Junho: '6',
  Julho: '7',
  Agosto: '8',
  Setembro: '9',
  Outubro: '10',
  Novembro: '11',
  Dezembro: '12',
};

const LEGACY_CATEGORY_VALUE_MAP: Record<string, TrainingCategory> = {
  'Pós-parto': 'postpartum',
  'Pós parto': 'postpartum',
  'Pos-parto': 'postpartum',
  'Pos parto': 'postpartum',
  Postpartum: 'postpartum',
  Gestante: 'pregnant',
  Iniciante: 'performance',
  Performance: 'performance',
  Reabilitacao: 'rehabilitation',
  Reabilitação: 'rehabilitation',
  'Perimenopausa / Menopausa': 'perimenopause_menopause',
  'Perimenopausa/Menopausa': 'perimenopause_menopause',
  'Perimenopausa/menopausa': 'perimenopause_menopause',
  Fullbody: 'fullbody',
  'Full body': 'fullbody',
  'Full Body': 'fullbody',
};

const LEGACY_OBJECTIVE_VALUE_MAP: Record<string, MainGoal> = {
  Emagrecimento: 'weight_loss',
  Hipertrofia: 'hypertrophy',
  Definicao: 'muscle_definition',
  'Definição muscular': 'muscle_definition',
  'Definição': 'muscle_definition',
  Resistencia: 'health_maintenance',
  'Resistência': 'health_maintenance',
  Adaptacao: 'health_maintenance',
  'Adaptação': 'health_maintenance',
  'Manutenção da saúde': 'health_maintenance',
  'Manutenção da saúde / reabilitação': 'health_maintenance',
};

const LEGACY_LOCATION_VALUE_MAP: Record<string, TrainingLocation> = {
  Academia: 'full_gym',
  'Academia completa': 'full_gym',
  Casa: 'home_condo_gym',
  'Casa / Academia do condomínio': 'home_condo_gym',
  'Casa ou academia de condomínio': 'home_condo_gym',
};

const LOCATION_OPTIONS: SelectOption[] = TRAINING_LOCATION_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const FREQUENCY_OPTIONS: SelectOption[] = [
  { value: '1', label: '1x por semana' },
  { value: '2', label: '2x por semana' },
  { value: '3', label: '3x por semana' },
  { value: '4', label: '4x por semana' },
  { value: '5', label: '5x por semana' },
  { value: '6', label: '6x por semana' },
  { value: '7', label: '7x por semana' },
];

const INITIAL_FORM_VALUES: TrainingFormValues = {
  name: '',
  levels: [],
  objective: [],
  month: '',
  description: '',
  location: '',
  frequency: '',
  duration: '',
  category: '',
};

const DEFAULT_EXECUTION_VALUES: TrainingExerciseExecution = {
  sets: 1,
  repetitions: 1,
  rest: DEFAULT_REST_SECONDS,
  method: '',
  details: [],
  methodConfig: null,
  repetitionScope: DEFAULT_REPETITION_SCOPE,
  ...emptyExecutionSpeedFields(),
  executionSeconds: null,
  exerciseType: null,
};

const DEFAULT_EXECUTION_DETAIL_LOAD_PERCENTAGE = null;

const METHOD_TYPE_LABELS_PT_BR: Record<string, string> = {
  training_structure: 'Estrutura do treino',
  intensity_methods: 'Métodos de intensidade',
  volume_progression_methods: 'Métodos de volume e progressão',
  tempo_conditioning_methods: 'Métodos de tempo e condicionamento',
};

const METHOD_TYPE_ORDER: Record<string, number> = {
  training_structure: 1,
  intensity_methods: 2,
  volume_progression_methods: 3,
  tempo_conditioning_methods: 4,
};

const formatMethodFallbackLabel = (methodName: string) => {
  const normalizedValue = methodName.trim();

  if (!normalizedValue) {
    return '';
  }

  if (['amrap', 'emom', 'hiit', 'rft', 'tabata'].includes(normalizedValue.toLowerCase())) {
    return normalizedValue.toUpperCase();
  }

  const titleizedValue = normalizedValue
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  return titleizedValue
    .replace(/^Bi Set$/i, 'Bi-set')
    .replace(/^Tri Set$/i, 'Tri-set')
    .replace(/^Drop Set$/i, 'Drop-set')
    .replace(/^Rest Pause$/i, 'Rest-pause')
    .replace(/^Multiple Set$/i, 'Série múltipla');
};

const normalizeMethodColor = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  const normalizedValue = trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;

  return /^#[0-9a-fA-F]{6}$/.test(normalizedValue) ? normalizedValue : null;
};

const normalizeMethodValue = (value?: string | null) => {
  return getCanonicalTrainingExecutionMethodValue(value);
};

const mapMethodLookupsToOptions = (lookups: ExerciseMethodLookup[]): ExecutionMethodOption[] => {
  if (lookups.length === 0) {
    return [];
  }

  const preferredByCanonical = new Map<string, ExerciseMethodLookup>();

  [...lookups]
    .sort((a, b) => {
      const typeOrderDiff = (METHOD_TYPE_ORDER[a.type] ?? 999) - (METHOD_TYPE_ORDER[b.type] ?? 999);

      if (typeOrderDiff !== 0) {
        return typeOrderDiff;
      }

      const labelA = getTrainingExecutionMethodDisplayLabel(a.nameEn, a.name.trim() || formatMethodFallbackLabel(a.nameEn ?? '')) || a.name.trim() || formatMethodFallbackLabel(a.nameEn ?? '');
      const labelB = getTrainingExecutionMethodDisplayLabel(b.nameEn, b.name.trim() || formatMethodFallbackLabel(b.nameEn ?? '')) || b.name.trim() || formatMethodFallbackLabel(b.nameEn ?? '');

      return labelA.localeCompare(labelB, 'pt-BR');
    })
    .forEach((item) => {
      const canonicalValue = getCanonicalTrainingExecutionMethodValue(item.nameEn?.trim() || item.name);

      if (!canonicalValue) {
        return;
      }

      const existing = preferredByCanonical.get(canonicalValue);
      const itemMatchesCanonical = normalizeTrainingExecutionMethodKey(item.nameEn) === canonicalValue;
      const existingMatchesCanonical = existing
        ? normalizeTrainingExecutionMethodKey(existing.nameEn) === canonicalValue
        : false;

      if (!existing || (itemMatchesCanonical && !existingMatchesCanonical)) {
        preferredByCanonical.set(canonicalValue, item);
      }
    });

  return Array.from(preferredByCanonical.entries()).map(([canonicalValue, item]) => {
    const fallbackLabel = item.name.trim() || formatMethodFallbackLabel(item.nameEn ?? '');

    return {
      id: item.id,
      value: canonicalValue,
      label: getTrainingExecutionMethodDisplayLabel(canonicalValue, fallbackLabel) || fallbackLabel,
      group: METHOD_TYPE_LABELS_PT_BR[item.type] ?? item.type,
      color: normalizeMethodColor(item.color),
      textColor: normalizeMethodColor(item.textColor),
    };
  });
};

const createIdentifier = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const parsePersistedEntityId = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const resolveMuscleGroups = (group: string, explicitGroups: string[] = []) => resolveTrainingExerciseMuscleGroups(group, explicitGroups);

interface SummaryMetricValues {
  totalVolume: string;
  totalTime: string;
  loadPercentage: string;
  loadIntensity: string;
  effortPerception: string;
}

const buildEmptyTrainingSummary = (blocks: TrainingBlock[]): TrainingSummaryResponse => ({
  ...EMPTY_SUMMARY_VALUES,
  blockSummaries: blocks.map((block) => ({
    blockId: block.id,
    ...EMPTY_SUMMARY_VALUES,
  })),
  muscleVolumes: [],
});

const buildEmptyTrainingSummaryFromRequest = (payload: TrainingSummaryRequestPayload): TrainingSummaryResponse => ({
  ...EMPTY_SUMMARY_VALUES,
  blockSummaries: payload.blocks.map((block) => ({
    blockId: block.id,
    ...EMPTY_SUMMARY_VALUES,
  })),
  muscleVolumes: [],
});

const buildSummaryMetrics = ({
  totalVolume,
  totalTime,
  loadPercentage,
  loadIntensity,
  effortPerception,
}: SummaryMetricValues): TrainingSummaryMetric[] => ([
  { label: 'Volume total', value: totalVolume },
  { label: 'Tempo total', value: totalTime },
  { label: 'Carga', value: loadPercentage },
  { label: 'Intensidade', value: loadIntensity },
  { label: 'Percepção de esforço', value: effortPerception },
]);

const flattenBlockExercises = (items: TrainingBlockExercise[]): TrainingBlockExercise[] => items.flatMap((item) => [
  item,
  ...flattenBlockExercises(item.conjugatedExercises ?? []),
]);

const normalizeGroupingToken = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const buildExerciseGroupSignature = (exercise: Pick<TrainingBlockExercise, 'exerciseId' | 'group' | 'muscleGroups'>) => {
  const normalizedGroups = Array.from(new Set((exercise.muscleGroups ?? [])
    .map((group) => normalizeGroupingToken(group))
    .filter(Boolean))).sort();

  if (normalizedGroups.length > 0) {
    return normalizedGroups.join('|');
  }

  const fallbackGroup = normalizeGroupingToken(exercise.group);

  return fallbackGroup || `exercise-${exercise.exerciseId}`;
};

const resolveExerciseGroupingType = (
  exercise: Pick<TrainingBlockExercise, 'exerciseId' | 'group' | 'muscleGroups'>,
  conjugatedExercises: Array<Pick<TrainingBlockExercise, 'exerciseId' | 'group' | 'muscleGroups'>>,
): TrainingExerciseGroupingType | null => {
  const groupedExercises = [exercise, ...conjugatedExercises];
  const groupedExercisesCount = groupedExercises.length;

  if (groupedExercisesCount < 2) {
    return null;
  }

  const uniqueGroupCount = new Set(groupedExercises.map((item) => buildExerciseGroupSignature(item))).size;

  if (uniqueGroupCount <= 1) {
    if (groupedExercisesCount === 2) {
      return 'bi_set';
    }

    if (groupedExercisesCount === 3) {
      return 'tri_set';
    }

    return 'super_set';
  }

  if (groupedExercisesCount === 2) {
    return 'conjugate';
  }

  return 'circuit';
};

const applyGroupingTypeToExerciseTree = (
  items: TrainingBlockExercise[],
  groupingType: TrainingExerciseGroupingType,
  tabataSourceExecution: TrainingExerciseExecution | null = null,
): TrainingBlockExercise[] => items.map((item) => ({
  ...item,
  groupingType,
  execution: tabataSourceExecution ? syncTabataExecution(tabataSourceExecution, item.execution) : item.execution,
  conjugatedExercises: applyGroupingTypeToExerciseTree(item.conjugatedExercises ?? [], groupingType, tabataSourceExecution),
}));

const syncExerciseGroupingTree = (
  items: TrainingBlockExercise[],
  inheritedGroupingType: TrainingExerciseGroupingType | null = null,
): TrainingBlockExercise[] => items.map((item) => {
  const rawConjugatedExercises = item.conjugatedExercises ?? [];
  const normalizedConjugatedExercises = syncExerciseGroupingTree(rawConjugatedExercises);
  const resolvedGroupingType = resolveExerciseGroupingType(item, normalizedConjugatedExercises);
  const effectiveGroupingType = resolvedGroupingType ?? inheritedGroupingType ?? 'multiple_set';
  const tabataSourceExecution = resolveTabataSourceExecution(item, normalizedConjugatedExercises, effectiveGroupingType);
  const groupedConjugatedExercises = applyGroupingTypeToExerciseTree(
    normalizedConjugatedExercises,
    effectiveGroupingType,
    tabataSourceExecution,
  );

  if (!tabataSourceExecution) {
    return {
      ...item,
      groupingType: effectiveGroupingType,
      conjugatedExercises: groupedConjugatedExercises,
    };
  }

  return {
    ...item,
    execution: syncTabataExecution(tabataSourceExecution, item.execution),
    groupingType: effectiveGroupingType,
    conjugatedExercises: groupedConjugatedExercises,
  };
});

const syncBlocksGroupingState = (blocks: TrainingBlock[]): TrainingBlock[] => blocks.map((block) => ({
  ...block,
  items: syncExerciseGroupingTree(block.items),
}));

const sanitizeExecutionMethodsInTree = (
  items: TrainingBlockExercise[],
  validMethods: Set<string>,
): { items: TrainingBlockExercise[]; changed: boolean } => {
  let changed = false;

  const nextItems = items.map((item) => {
    const childResult = sanitizeExecutionMethodsInTree(item.conjugatedExercises ?? [], validMethods);
    const normalizedMethod = normalizeMethodValue(item.execution.method);
    const nextMethod = normalizedMethod && !validMethods.has(normalizedMethod) ? '' : normalizedMethod;
    const methodChanged = nextMethod !== item.execution.method;

    if (!methodChanged && !childResult.changed) {
      return item;
    }

    changed = true;

    return {
      ...item,
      execution: methodChanged ? normalizeExecution({ ...item.execution, method: nextMethod }) : item.execution,
      conjugatedExercises: childResult.changed ? childResult.items : item.conjugatedExercises,
    };
  });

  return { items: nextItems, changed };
};

const buildSummaryRequestPayload = (blocks: TrainingBlock[], trainingLevels: TrainingLevel[]): TrainingSummaryRequestPayload => ({
  blocks: blocks.map((block) => ({
    id: block.id,
    items: flattenBlockExercises(block.items).map((item) => ({
      exerciseId: item.exerciseId,
      execution: {
        sets: item.execution.sets,
        repetitions: item.execution.repetitions,
        rest: item.execution.rest,
        method: item.execution.method,
        details: item.execution.details.map((detail) => ({
          repetitionsMin: detail.repetitionsMin,
          repetitionsMax: detail.repetitionsMax,
          loadPercentage: detail.loadPercentage,
          rest: detail.rest,
          block: detail.block,
        })),
        methodConfig: item.execution.methodConfig ?? null,
        ...buildExecutionSpeedFields(item.execution),
        executionSeconds: item.execution.executionSeconds ?? null,
        exerciseType: item.execution.exerciseType ?? null,
      },
    })),
  })),
  trainingLevel: resolvePrimaryWorkoutLevel(trainingLevels),
  trainingLevels,
});

const buildSummarySignature = (blocks: TrainingBlock[], trainingLevels: TrainingLevel[]) => JSON.stringify(buildSummaryRequestPayload(blocks, trainingLevels));

const normalizePositiveIntegerValue = (value?: string | number | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : null;
  }

  const digits = value?.trim().match(/\d+/)?.[0] ?? '';

  if (!digits) {
    return null;
  }

  const normalizedValue = digits.replace(/^0+/, '');

  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number.parseInt(normalizedValue, 10);

  return parsedValue > 0 ? parsedValue : null;
};

const normalizePositiveRangeValue = (value?: string | number | null) => {
  return normalizePositiveIntegerValue(value);
};

const buildNormalizedRepetitionRange = (value?: string | number | null) => {
  const normalizedValue = normalizePositiveRangeValue(value) ?? DEFAULT_EXECUTION_VALUES.repetitions ?? 1;

  return {
    min: normalizedValue,
    max: normalizedValue,
  };
};

const normalizeLoadPercentageValue = (value?: string | number | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= 100 ? Math.round(value) : null;
  }

  const digits = value?.trim().match(/\d+/)?.[0] ?? '';

  if (!digits) {
    return null;
  }

  const parsedValue = Number.parseInt(digits, 10);

  if (!Number.isInteger(parsedValue) || parsedValue < 0 || parsedValue > 100) {
    return null;
  }

  return parsedValue;
};

const hasOwnField = <T extends object>(value: T | undefined, field: keyof T) => Boolean(value && Object.prototype.hasOwnProperty.call(value, field));

const normalizeOptionalPositiveIntegerNumber = (value?: string | number | null) => normalizePositiveIntegerValue(value);

const buildTabataExecution = (execution?: Partial<TrainingExerciseExecution>): TrainingExerciseExecution => normalizeExecution({
  ...execution,
  method: 'tabata',
  repetitions: null,
  details: [],
  methodConfig: null,
});

const syncTabataExecution = (
  sourceExecution: TrainingExerciseExecution,
  targetExecution?: TrainingExerciseExecution,
): TrainingExerciseExecution => buildTabataExecution({
  ...(targetExecution ?? {}),
  sets: sourceExecution.sets,
  rest: sourceExecution.rest,
  executionSeconds: sourceExecution.executionSeconds,
});

const resolveTabataSourceExecution = (
  item: TrainingBlockExercise,
  conjugatedExercises: TrainingBlockExercise[],
  groupingType: TrainingExerciseGroupingType,
) => {
  if (!isTabataSupportedTrainingGroupingType(groupingType) || conjugatedExercises.length === 0) {
    return null;
  }

  const groupedExercises = [item, ...conjugatedExercises];
  const sourceExercise = groupedExercises.find((groupedExercise) => isTabataTrainingExecutionMethod(groupedExercise.execution.method));

  if (!sourceExercise) {
    return null;
  }

  return buildTabataExecution(sourceExercise.execution);
};

const isDropSetMethod = (value?: string | null) => isDropFamilyTrainingExecutionMethod(value);

const isRestPauseMethod = (value?: string | null) => isRestPauseTrainingExecutionMethod(value);

const normalizeExecutionMethodConfig = (
  methodConfig: TrainingExerciseMethodConfig | null | undefined,
  method: string,
): TrainingExerciseMethodConfig | null => {
  const methodDefinition = getTrainingExecutionMethodDefinition(method);

  if (methodDefinition.family === 'cluster_set') {
    return {
      roundRepetitions: normalizeOptionalPositiveIntegerNumber(methodConfig?.roundRepetitions),
      intraSetRest: normalizeOptionalPositiveIntegerNumber(methodConfig?.intraSetRest),
      fixedRestMin: null,
      fixedRestMax: null,
    };
  }

  if (methodDefinition.family === 'gvt') {
    return {
      fixedRestMin: 30,
      fixedRestMax: 60,
    };
  }

  return null;
};

const normalizeExecutionDetailsByMethod = (
  details: TrainingExerciseExecutionDetail[],
  method: string,
  fallbackRest: number | null,
) => details.map((detail) => {
  const detailRepetitionsMin = normalizeOptionalPositiveIntegerNumber(detail.repetitionsMin);
  const detailRepetitionsMax = normalizeOptionalPositiveIntegerNumber(detail.repetitionsMax);
  const resolvedSingleRepetition = detailRepetitionsMax ?? detailRepetitionsMin;
  const prescribedLoadPercentage = usesPrescribedLoadPercentage(method)
    ? getPyramidLoadPercentageValue(resolvedSingleRepetition)
    : null;

  if (isDropSetMethod(method)) {
    return {
      ...detail,
      repetitionsMin: resolvedSingleRepetition,
      repetitionsMax: resolvedSingleRepetition,
      loadPercentage: prescribedLoadPercentage ?? detail.loadPercentage,
      rest: detail.rest ?? fallbackRest,
    };
  }

  if (isRestPauseMethod(method)) {
    const normalizedMin = detailRepetitionsMin ?? detailRepetitionsMax;
    const normalizedMax = detailRepetitionsMax ?? detailRepetitionsMin;

    return {
      ...detail,
      repetitionsMin: normalizedMin,
      repetitionsMax: normalizedMax,
      loadPercentage: prescribedLoadPercentage ?? detail.loadPercentage,
      rest: detail.rest ?? fallbackRest,
    };
  }

  const normalizedMin = detailRepetitionsMin ?? detailRepetitionsMax;
  const normalizedMax = detailRepetitionsMax ?? detailRepetitionsMin;

  return {
    ...detail,
    repetitionsMin: normalizedMin,
    repetitionsMax: normalizedMax,
    loadPercentage: prescribedLoadPercentage ?? detail.loadPercentage,
    rest: detail.rest ?? fallbackRest,
  };
});

const normalizeExecutionDetail = (
  detail: Partial<TrainingExerciseExecutionDetail> | undefined,
  index: number,
  fallbackRepetitions: number | null,
  fallbackLoadPercentage: number | null = DEFAULT_EXECUTION_DETAIL_LOAD_PERCENTAGE,
  fallbackRest: number | null = DEFAULT_EXECUTION_VALUES.rest,
): TrainingExerciseExecutionDetail => {
  const repetitionRange = buildNormalizedRepetitionRange(detail?.repetitionsMax ?? detail?.repetitionsMin ?? fallbackRepetitions);

  return {
    id: detail?.id?.trim() || createIdentifier('training-execution-detail'),
    repetitionsMin: hasOwnField(detail, 'repetitionsMin') ? normalizeOptionalPositiveIntegerNumber(detail?.repetitionsMin) : repetitionRange.min,
    repetitionsMax: hasOwnField(detail, 'repetitionsMax') ? normalizeOptionalPositiveIntegerNumber(detail?.repetitionsMax) : repetitionRange.max,
    loadPercentage: hasOwnField(detail, 'loadPercentage')
      ? normalizeLoadPercentageValue(detail?.loadPercentage)
      : fallbackLoadPercentage,
    rest: hasOwnField(detail, 'rest')
      ? normalizeOptionalPositiveIntegerNumber(detail?.rest)
      : fallbackRest,
    block: normalizePositiveIntegerValue(detail?.block) ?? (index + 1),
  };
};

const resolveExecutionDetailCount = (
  setsValue: number | null,
  normalizedDetails: TrainingExerciseExecutionDetail[],
  method: string,
) => {
  if (isPyramidTrainingExecutionMethod(method)) {
    return setsValue && setsValue > 0 ? setsValue : (normalizedDetails.length > 0 ? normalizedDetails.length : 1);
  }

  if (isDropSetMethod(method)) {
    return normalizedDetails.length > 0 ? normalizedDetails.length : 1;
  }

  return setsValue && setsValue > 0
    ? setsValue
    : (DEFAULT_EXECUTION_VALUES.sets ?? 1);
};

const syncExecutionDetails = (
  details: TrainingExerciseExecutionDetail[] | undefined,
  setsValue: number | null,
  repetitionsValue: number | null,
  fallbackLoadPercentage: number | null = DEFAULT_EXECUTION_DETAIL_LOAD_PERCENTAGE,
  fallbackRest: number | null = DEFAULT_EXECUTION_VALUES.rest,
  method = '',
): TrainingExerciseExecutionDetail[] => {
  const normalizedDetails = (details ?? []).map((detail, index) => normalizeExecutionDetail(
    detail,
    index,
    repetitionsValue,
    fallbackLoadPercentage,
    fallbackRest,
  ));
  const detailCount = resolveExecutionDetailCount(setsValue, normalizedDetails, method);
  const seedDetail = normalizedDetails[normalizedDetails.length - 1] ?? normalizeExecutionDetail(
    undefined,
    0,
    repetitionsValue,
    fallbackLoadPercentage,
    fallbackRest,
  );

  return Array.from({ length: detailCount }, (_, index) => {
    const existingDetail = normalizedDetails[index];

    if (existingDetail) {
      return existingDetail;
    }

    return {
      ...seedDetail,
      id: createIdentifier('training-execution-detail'),
      block: index + 1,
    };
  });
};

const buildRepetitionsFromExecutionDetails = (details: TrainingExerciseExecutionDetail[]) => {
  if (details.length === 0) {
    return null;
  }

  const repetitionValues = details.flatMap((detail) => {
    return [detail.repetitionsMin, detail.repetitionsMax].filter((value): value is number => value !== null && Number.isInteger(value) && value > 0);
  });

  if (repetitionValues.length === 0) {
    return null;
  }

  const maxValue = Math.max(...repetitionValues);

  return maxValue;
};

const buildRestFromExecutionDetails = (details: TrainingExerciseExecutionDetail[]) => details.find((detail) => (detail.rest ?? 0) > 0)?.rest ?? null;

const normalizeExecution = (execution?: Partial<TrainingExerciseExecution>): TrainingExerciseExecution => {
  const normalizedMethod = normalizeMethodValue(execution?.method);
  const methodDefinition = getTrainingExecutionMethodDefinition(normalizedMethod);
  const normalizedSets = methodDefinition.fixedSets ?? normalizePositiveIntegerValue(execution?.sets) ?? DEFAULT_EXECUTION_VALUES.sets;
  const normalizedExecutionSeconds = normalizeOptionalPositiveIntegerNumber(execution?.executionSeconds);
  const persistedSetIds = Array.from(new Set(
    (execution?.persistedSetIds ?? []).filter((id) => Number.isInteger(id) && id > 0),
  ));
  const speedFields = buildExecutionSpeedFields(execution);
  const isIsometry = isIsometryExecutionSpeed(speedFields.executionSpeed);

  if (isTabataTrainingExecutionMethod(normalizedMethod)) {
    return {
      sets: normalizedSets,
      repetitions: null,
      rest: normalizePositiveIntegerValue(execution?.rest) ?? DEFAULT_EXECUTION_VALUES.rest,
      method: normalizedMethod,
      details: [],
      persistedSetIds,
      methodConfig: null,
      repetitionScope: normalizeRepetitionScope(execution?.repetitionScope),
      ...speedFields,
      executionSeconds: normalizedExecutionSeconds,
      exerciseType: execution?.exerciseType ?? DEFAULT_EXECUTION_VALUES.exerciseType,
    };
  }

  const normalizedRepetitions = isIsometry
    ? null
    : (methodDefinition.fixedRepetitions
      ?? normalizePositiveRangeValue(execution?.repetitions)
      ?? buildRepetitionsFromExecutionDetails(execution?.details ?? [])
      ?? DEFAULT_EXECUTION_VALUES.repetitions);
  const normalizedRest = methodDefinition.fixedRest ?? normalizePositiveIntegerValue(execution?.rest) ?? DEFAULT_EXECUTION_VALUES.rest;
  const normalizedDetails = normalizeExecutionDetailsByMethod(syncExecutionDetails(
    execution?.details,
    normalizedSets,
    isIsometry ? 0 : (normalizedRepetitions ?? DEFAULT_EXECUTION_VALUES.repetitions),
    DEFAULT_EXECUTION_DETAIL_LOAD_PERCENTAGE,
    normalizedRest,
    normalizedMethod,
  ), normalizedMethod, normalizedRest).map((detail) => (
    isIsometry
      ? { ...detail, repetitionsMin: null, repetitionsMax: null }
      : detail
  ));

  return {
    sets: normalizedSets,
    repetitions: isIsometry ? null : (buildRepetitionsFromExecutionDetails(normalizedDetails) ?? normalizedRepetitions),
    rest: normalizePositiveIntegerValue(execution?.rest) ?? buildRestFromExecutionDetails(normalizedDetails) ?? DEFAULT_EXECUTION_VALUES.rest,
    method: normalizedMethod,
    details: normalizedDetails,
    persistedSetIds,
    methodConfig: normalizeExecutionMethodConfig(execution?.methodConfig, normalizedMethod),
    repetitionScope: normalizeRepetitionScope(execution?.repetitionScope),
    ...speedFields,
    executionSeconds: normalizedExecutionSeconds,
    exerciseType: execution?.exerciseType ?? DEFAULT_EXECUTION_VALUES.exerciseType,
  };
};

const buildRepetitionsFromDefaults = (min?: number | null, max?: number | null) => {
  if (typeof min === 'number' && typeof max === 'number') {
    return Math.max(min, max);
  }

  if (typeof min === 'number') {
    return min;
  }

  if (typeof max === 'number') {
    return max;
  }

  return null;
};

const buildRestFromDefaults = (restSeconds?: number | null) => {
  if (typeof restSeconds !== 'number' || restSeconds <= 0) {
    return null;
  }

  return restSeconds;
};

const buildExecutionFromExerciseRecord = (
  record: ExerciseRecord,
  executionMethodOptions: ExecutionMethodOption[],
  currentExecution: TrainingExerciseExecution,
  preferCurrentExecution: boolean,
): TrainingExerciseExecution => {
  const fallbackMethod = executionMethodOptions.find((option) => option.id === record.default_method_id)?.value ?? '';
  const repetitions = buildRepetitionsFromDefaults(record.default_repetitions_min, record.default_repetitions_max);
  const rest = buildRestFromDefaults(record.default_rest_sec);
  const loadPercentage = record.default_load_percent != null ? Math.round(record.default_load_percent) : DEFAULT_EXECUTION_DETAIL_LOAD_PERCENTAGE;
  const defaultSets = normalizePositiveIntegerValue(record.default_sets) ?? DEFAULT_EXECUTION_VALUES.sets;
  const defaultRepetitions = repetitions ?? DEFAULT_EXECUTION_VALUES.repetitions;
  const defaultRest = rest ?? DEFAULT_EXECUTION_VALUES.rest;
  const catalogSpeedFields = buildExecutionSpeedFields({
    executionSpeed: record.default_execution_speed,
    isometricSeconds: record.default_execution_sec,
    concentricSeconds: record.default_concentric_seconds,
    pauseSeconds: record.default_pause_seconds,
    eccentricSeconds: record.default_eccentric_seconds,
  });
  const defaultExecution = normalizeExecution({
    ...DEFAULT_EXECUTION_VALUES,
    sets: defaultSets,
    repetitions: defaultRepetitions,
    rest: defaultRest,
    method: fallbackMethod,
    details: syncExecutionDetails([], defaultSets, defaultRepetitions, loadPercentage, defaultRest, fallbackMethod),
    ...catalogSpeedFields,
    executionSeconds: record.default_execution_sec ?? null,
    exerciseType: record.exercise_type ?? null,
  });

  if (!preferCurrentExecution) {
    return defaultExecution;
  }

  return normalizeExecution({
    ...defaultExecution,
    ...currentExecution,
    sets: currentExecution.sets ?? defaultExecution.sets,
    repetitions: currentExecution.repetitions ?? defaultExecution.repetitions,
    rest: currentExecution.rest ?? defaultExecution.rest,
    method: currentExecution.method || defaultExecution.method,
    details: currentExecution.details.length > 0 ? currentExecution.details : defaultExecution.details,
    methodConfig: currentExecution.methodConfig ?? defaultExecution.methodConfig,
    ...buildExecutionSpeedFields({
      executionSpeed: currentExecution.executionSpeed ?? defaultExecution.executionSpeed,
      isometricSeconds: currentExecution.isometricSeconds ?? defaultExecution.isometricSeconds,
      concentricSeconds: currentExecution.concentricSeconds ?? defaultExecution.concentricSeconds,
      pauseSeconds: currentExecution.pauseSeconds ?? defaultExecution.pauseSeconds,
      eccentricSeconds: currentExecution.eccentricSeconds ?? defaultExecution.eccentricSeconds,
    }),
    executionSeconds: currentExecution.executionSeconds ?? defaultExecution.executionSeconds,
    exerciseType: currentExecution.exerciseType ?? defaultExecution.exerciseType,
  });
};

const createBlockExercise = (exercise: ExerciseItem): TrainingBlockExercise => ({
  id: createIdentifier('training-item'),
  exerciseId: exercise.id,
  name: exercise.name,
  group: exercise.group,
  objective: exercise.objective,
  execution: normalizeExecution({
    ...DEFAULT_EXECUTION_VALUES,
    ...buildExecutionSpeedFields({
      executionSpeed: exercise.defaultExecutionSpeed,
      isometricSeconds: exercise.defaultExecutionSeconds,
      concentricSeconds: exercise.defaultConcentricSeconds,
      pauseSeconds: exercise.defaultPauseSeconds,
      eccentricSeconds: exercise.defaultEccentricSeconds,
    }),
    executionSeconds: exercise.defaultExecutionSeconds ?? null,
    exerciseType: exercise.exerciseType ?? null,
  }),
  muscleGroups: resolveMuscleGroups(exercise.group),
  groupingType: null,
  conjugatedExercises: [],
  hasExerciseDefaultsLoaded: false,
});

const createEmptyBlock = (index: number, label?: string): TrainingBlock => ({
  id: createIdentifier('training-block'),
  label: label ?? BLOCK_LABELS[index] ?? `Bloco ${index + 1}`,
  items: [],
});

const normalizeBlockExerciseTree = (item: TrainingBlockExercise): TrainingBlockExercise => ({
  ...item,
  id: item.id || createIdentifier('training-item'),
  execution: normalizeExecution(item.execution),
  muscleGroups: item.muscleGroups?.length ? resolveMuscleGroups(item.group, item.muscleGroups) : resolveMuscleGroups(item.group),
  groupingType: item.groupingType ?? null,
  conjugatedExercises: (item.conjugatedExercises ?? []).map(normalizeBlockExerciseTree),
  hasExerciseDefaultsLoaded: true,
});

const findExerciseInTree = (items: TrainingBlockExercise[], exerciseId: string): TrainingBlockExercise | null => {
  for (const item of items) {
    if (item.id === exerciseId) {
      return item;
    }

    const nestedExercise = findExerciseInTree(item.conjugatedExercises ?? [], exerciseId);

    if (nestedExercise) {
      return nestedExercise;
    }
  }

  return null;
};

const containsExerciseId = (item: TrainingBlockExercise, targetExerciseId: string): boolean => {
  if (item.id === targetExerciseId) {
    return true;
  }

  return (item.conjugatedExercises ?? []).some((child) => containsExerciseId(child, targetExerciseId));
};

const updateExerciseTree = (
  items: TrainingBlockExercise[],
  exerciseId: string,
  updater: (exercise: TrainingBlockExercise) => TrainingBlockExercise,
): { nextItems: TrainingBlockExercise[]; updated: boolean } => {
  let updated = false;

  const nextItems = items.map((item) => {
    if (item.id === exerciseId) {
      updated = true;
      return updater(item);
    }

    const childUpdate = updateExerciseTree(item.conjugatedExercises ?? [], exerciseId, updater);

    if (!childUpdate.updated) {
      return item;
    }

    updated = true;
    return {
      ...item,
      conjugatedExercises: childUpdate.nextItems,
    };
  });

  return { nextItems, updated };
};

const buildUnconfiguredExecution = (execution: TrainingExerciseExecution): TrainingExerciseExecution => normalizeExecution({
  ...DEFAULT_EXECUTION_VALUES,
  ...buildExecutionSpeedFields(execution),
  persistedSetIds: execution.persistedSetIds,
  exerciseType: execution.exerciseType ?? null,
});

const collectExerciseTreeIds = (items: TrainingBlockExercise[]): string[] => items.flatMap((item) => ([
  item.id,
  ...collectExerciseTreeIds(item.conjugatedExercises ?? []),
]));

const resetExerciseTreeToUnconfiguredDefaults = (items: TrainingBlockExercise[]): TrainingBlockExercise[] => items.map((item) => ({
  ...item,
  execution: buildUnconfiguredExecution(item.execution),
  conjugatedExercises: resetExerciseTreeToUnconfiguredDefaults(item.conjugatedExercises ?? []),
  hasExerciseDefaultsLoaded: false,
}));

const insertExerciseAtTopLevel = (
  items: TrainingBlockExercise[],
  exercise: TrainingBlockExercise,
  targetExerciseId?: string,
  placement: 'before' | 'after' = 'before',
) => {
  const nextItems = [...items];
  const targetIndex = targetExerciseId
    ? nextItems.findIndex((item) => item.id === targetExerciseId)
    : -1;

  if (targetIndex < 0) {
    nextItems.push(exercise);
    return nextItems;
  }

  const insertionIndex = placement === 'after' ? targetIndex + 1 : targetIndex;
  nextItems.splice(insertionIndex, 0, exercise);

  return nextItems;
};

const attachExerciseAsConjugate = (
  items: TrainingBlockExercise[],
  parentExerciseId: string,
  exercise: TrainingBlockExercise,
): { nextItems: TrainingBlockExercise[]; attached: boolean } => {
  let attached = false;
  const exercisesToAttach = flattenExerciseCluster(exercise);
  const clusterRoot = items.find((item) => containsExerciseId(item, parentExerciseId));
  const resolvedParentId = clusterRoot?.id ?? parentExerciseId;

  const nextItems = items.map((item) => {
    if (item.id !== resolvedParentId) {
      return item;
    }

    attached = true;

    return {
      ...item,
      conjugatedExercises: [...(item.conjugatedExercises ?? []), ...exercisesToAttach],
    };
  });

  return { nextItems, attached };
};

const mapLoadIntensityToLevel = (loadIntensity?: string) => {
  if (loadIntensity === 'Alta') {
    return 'advanced';
  }

  if (loadIntensity === 'Moderada') {
    return 'intermediate';
  }

  if (loadIntensity === 'Baixa' || loadIntensity === 'Leve') {
    return 'beginner';
  }

  return '';
};

const normalizeNumericValue = (value?: string | number | null) => {
  if (typeof value === 'number') {
    return String(value);
  }

  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return '';
  }

  return trimmedValue.match(/\d+/)?.[0] ?? '';
};

const normalizeCategoryValue = (value?: string | null): TrainingFormValues['category'] => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return '';
  }

  if (TRAINING_CATEGORY_OPTIONS.some((option) => option.value === trimmedValue)) {
    return trimmedValue as TrainingCategory;
  }

  return LEGACY_CATEGORY_VALUE_MAP[trimmedValue] ?? '';
};

const normalizeLevelsValue = (training?: TrainingItem | TrainingRecord): TrainingLevel[] => {
  if (training?.levels && training.levels.length > 0) {
    return expandLegacyWorkoutLevels(training.levels);
  }

  return expandLegacyWorkoutLevels(training?.level ?? mapLoadIntensityToLevel(training?.loadIntensity));
};

const normalizeObjectiveEntry = (value?: string | null): MainGoal | null => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  if (MAIN_GOAL_OPTIONS.some((option) => option.value === trimmedValue)) {
    return trimmedValue as MainGoal;
  }

  return LEGACY_OBJECTIVE_VALUE_MAP[trimmedValue] ?? null;
};

const normalizeObjectiveValue = (value?: string | string[] | null): TrainingFormValues['objective'] => {
  const rawValues = Array.isArray(value)
    ? value
    : (value?.trim() ?? '')
      ? value!.split(/\s*[•,;]\s*/)
      : [];

  return Array.from(new Set(
    rawValues
      .map((entry) => normalizeObjectiveEntry(entry))
      .filter((entry): entry is MainGoal => entry !== null)
  ));
};

const formatObjectiveLabels = (values: MainGoal[]) => values
  .map((value) => MAIN_GOAL_LABELS[value] ?? value)
  .join(' • ');

const normalizeLocationValue = (value?: string | null): TrainingFormValues['location'] => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return '';
  }

  if (TRAINING_LOCATION_OPTIONS.some((option) => option.value === trimmedValue)) {
    return trimmedValue as TrainingLocation;
  }

  return LEGACY_LOCATION_VALUE_MAP[trimmedValue] ?? '';
};

const normalizeMonthValue = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return '';
  }

  const normalizedMonthMatch = trimmedValue.match(/^m[eê]s\s*(\d{1,2})$/i);

  if (normalizedMonthMatch) {
    const monthNumber = Number.parseInt(normalizedMonthMatch[1], 10);

    if (monthNumber >= 1 && monthNumber <= 24) {
      return String(monthNumber);
    }
  }

  const legacyMonthValue = LEGACY_MONTH_VALUE_MAP[trimmedValue];

  if (legacyMonthValue) {
    return legacyMonthValue;
  }

  const numericMonthValue = Number.parseInt(normalizeNumericValue(trimmedValue), 10);
  return numericMonthValue >= 1 && numericMonthValue <= 24 ? String(numericMonthValue) : '';
};

const buildFormValues = (training?: TrainingItem | TrainingRecord): TrainingFormValues => {
  if (!training) {
    return INITIAL_FORM_VALUES;
  }

  return {
    name: training.name,
    levels: normalizeLevelsValue(training),
    objective: normalizeObjectiveValue(training.objective ?? training.descriptionLabel),
    month: normalizeMonthValue(training.monthIndex ? String(training.monthIndex) : training.month),
    description: training.description,
    location: normalizeLocationValue(training.location),
    frequency: normalizeNumericValue(training.sessionsPerWeek ?? training.frequency),
    duration: normalizeNumericValue(training.durationMinutes ?? training.duration),
    category: normalizeCategoryValue(training.category ?? training.status),
  };
};

const buildBlocks = (training?: TrainingItem | TrainingRecord): TrainingBlock[] => {
  if (training && 'blocks' in training && training.blocks.length > 0) {
    return syncBlocksGroupingState(training.blocks.map((block, index) => ({
      id: block.id || createIdentifier('training-block'),
      label: block.label || BLOCK_LABELS[index] || `Bloco ${index + 1}`,
      items: block.items.map(normalizeBlockExerciseTree),
    })));
  }

  if (training?.splits.length) {
    return training.splits.map((label, index) => createEmptyBlock(index, label));
  }

  return [createEmptyBlock(0, 'A')];
};

const findBlockExercise = (blocks: TrainingBlock[], blockId: string, exerciseId: string) => blocks
  .find((block) => block.id === blockId)
  ?.items.reduce<TrainingBlockExercise | null>((foundExercise, item) => foundExercise ?? findExerciseInTree([item], exerciseId), null) ?? null;

const reorderBlockItems = (
  blocks: TrainingBlock[],
  blockId: string,
  exerciseId: string,
  direction: 'left' | 'right'
) => blocks.map((block) => {
  if (block.id !== blockId) {
    return block;
  }

  const currentIndex = block.items.findIndex((item) => item.id === exerciseId);

  if (currentIndex < 0) {
    return block;
  }

  const nextIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;

  if (nextIndex < 0 || nextIndex >= block.items.length) {
    return block;
  }

  const nextItems = [...block.items];
  const [movedItem] = nextItems.splice(currentIndex, 1);
  nextItems.splice(nextIndex, 0, movedItem);

  return {
    ...block,
    items: nextItems,
  };
});

const insertExerciseIntoBlocks = (
  blocks: TrainingBlock[],
  blockId: string,
  exercise: TrainingBlockExercise,
  targetExerciseId?: string,
  dropMode: ExerciseDropMode = 'insert'
) => {
  return {
    nextBlocks: syncBlocksGroupingState(blocks.map((block) => {
      if (block.id !== blockId) {
        return block;
      }

      if (dropMode === 'conjugate' && targetExerciseId) {
        const attachedItems = attachExerciseAsConjugate(block.items, targetExerciseId, exercise);

        if (attachedItems.attached) {
          return {
            ...block,
            items: attachedItems.nextItems,
          };
        }
      }

      return {
        ...block,
        items: insertExerciseAtTopLevel(
          block.items,
          exercise,
          targetExerciseId,
          dropMode === 'insert-after' ? 'after' : 'before',
        ),
      };
    })),
  };
};

const moveExerciseAcrossBlocks = (
  blocks: TrainingBlock[],
  sourceBlockId: string,
  exerciseId: string,
  targetBlockId: string,
  targetExerciseId?: string,
  dropMode: ExerciseDropMode = 'insert'
) => {
  if (sourceBlockId === targetBlockId && exerciseId === targetExerciseId) {
    return blocks;
  }

  if (sourceBlockId === targetBlockId && targetExerciseId && dropMode !== 'conjugate') {
    const sourceBlock = blocks.find((block) => block.id === sourceBlockId);

    if (sourceBlock) {
      const reorderedItems = reorderExercisesInSameCluster(
        sourceBlock.items,
        exerciseId,
        targetExerciseId,
        dropMode === 'insert-after' ? 'after' : 'before',
      );

      if (reorderedItems) {
        return syncBlocksGroupingState(blocks.map((block) => (
          block.id === sourceBlockId
            ? { ...block, items: reorderedItems }
            : block
        )));
      }
    }
  }

  if (sourceBlockId === targetBlockId && targetExerciseId && dropMode === 'conjugate') {
    const sourceBlock = blocks.find((block) => block.id === sourceBlockId);
    const clusterRoot = sourceBlock?.items.find((item) => (
      containsExerciseId(item, exerciseId) && containsExerciseId(item, targetExerciseId)
    ));

    if (clusterRoot) {
      return blocks;
    }
  }

  let movedExercise: TrainingBlockExercise | null = null;

  const blocksWithoutDraggedExercise = blocks.map((block) => {
    if (block.id !== sourceBlockId) {
      return block;
    }

    const removal = extractExerciseFromTree(block.items, exerciseId);

    if (!removal.extractedExercise) {
      return block;
    }

    movedExercise = removal.extractedExercise;

    return {
      ...block,
      items: removal.nextItems,
    };
  });

  if (!movedExercise) {
    return blocks;
  }

  if (dropMode === 'conjugate') {
    if (!targetExerciseId || containsExerciseId(movedExercise, targetExerciseId)) {
      return blocks;
    }

    const nextBlocks = blocksWithoutDraggedExercise.map((block) => {
      if (block.id !== targetBlockId) {
        return block;
      }

      const attachedItems = attachExerciseAsConjugate(block.items, targetExerciseId, movedExercise as TrainingBlockExercise);

      if (!attachedItems.attached) {
        return block;
      }

      return {
        ...block,
        items: attachedItems.nextItems,
      };
    });

    return syncBlocksGroupingState(nextBlocks);
  }

  return syncBlocksGroupingState(blocksWithoutDraggedExercise.map((block) => {
    if (block.id !== targetBlockId) {
      return block;
    }

    return {
      ...block,
      items: insertExerciseAtTopLevel(
        block.items,
        movedExercise as TrainingBlockExercise,
        targetExerciseId,
        dropMode === 'insert-after' ? 'after' : 'before',
      ),
    };
  }));
};

const useTrainingBuilder = (
  trainingId?: string,
  storage: TrainingBuilderStorage = TrainingService,
): UseTrainingBuilderResult => {
  const [training, setTraining] = useState<TrainingRecord | undefined>(undefined);
  const [isLoadingTraining, setIsLoadingTraining] = useState(Boolean(trainingId));
  const [isMissingTraining, setIsMissingTraining] = useState(false);
  const [formValues, setFormValues] = useState<TrainingFormValues>(() => buildFormValues(training));
  const [errors, setErrors] = useState<TrainingFormErrors>({});
  const [blocks, setBlocks] = useState<TrainingBlock[]>(() => buildBlocks(training));
  const [submitError, setSubmitError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const {
    filters: {
      catalogSearch: catalogSearchTerm,
      catalogGroups,
    },
    setFilter: setCatalogFilter,
    setFilters: setCatalogFilters,
  } = useQueryFilters(TRAINING_CATALOG_QUERY_SCHEMA);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseItem | null>(null);
  const [activeExerciseConfig, setActiveExerciseConfig] = useState<ActiveExerciseConfig | null>(null);
  const [trainingSummary, setTrainingSummary] = useState<TrainingSummaryResponse>(() => buildEmptyTrainingSummary(buildBlocks(training)));
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [executionMethodOptions, setExecutionMethodOptions] = useState<ExecutionMethodOption[]>([]);
  const summaryRequestIdRef = useRef(0);
  const removedBlockIdsRef = useRef(new Set<number>());
  const removedExerciseIdsRef = useRef(new Set<number>());
  const {
    exercises: catalogExercises,
    loading: catalogLoading,
    groupOptions: catalogGroupOptions,
  } = useExercises(1, CATALOG_PAGE_SIZE, {
    searchTerm: catalogSearchTerm,
    groups: catalogGroups,
  });

  useEffect(() => {
    let isMounted = true;

    const loadTraining = async () => {
      if (!trainingId) {
        setTraining(undefined);
        setIsMissingTraining(false);
        setIsLoadingTraining(false);
        return;
      }

      setIsLoadingTraining(true);

      try {
        const loadedTraining = await storage.getTrainingById(trainingId);

        if (!isMounted) {
          return;
        }

        setTraining(loadedTraining ?? undefined);
        setIsMissingTraining(!loadedTraining);
      } catch {
        if (!isMounted) {
          return;
        }

        setTraining(undefined);
        setIsMissingTraining(true);
      } finally {
        if (isMounted) {
          setIsLoadingTraining(false);
        }
      }
    };

    void loadTraining();

    return () => {
      isMounted = false;
    };
  }, [storage, trainingId]);

  useEffect(() => {
    let isMounted = true;

    const loadExecutionMethods = async () => {
      try {
        const lookups = await ExerciseService.getExerciseMethods();

        if (!isMounted) {
          return;
        }

        setExecutionMethodOptions(mapMethodLookupsToOptions(lookups));
      } catch {
        if (!isMounted) {
          return;
        }

        setExecutionMethodOptions([]);
      }
    };

    void loadExecutionMethods();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (executionMethodOptions.length === 0) {
      return;
    }

    const validMethods = new Set(executionMethodOptions.map((option) => option.value));

    setBlocks((currentBlocks) => {
      let hasChanges = false;
      const nextBlocks = currentBlocks.map((block) => {
        const sanitizedItems = sanitizeExecutionMethodsInTree(block.items, validMethods);

        if (!sanitizedItems.changed) {
          return block;
        }

        hasChanges = true;

        return {
          ...block,
          items: syncExerciseGroupingTree(sanitizedItems.items),
        };
      });

      return hasChanges ? nextBlocks : currentBlocks;
    });
  }, [executionMethodOptions]);

  useEffect(() => {
    const nextBlocks = buildBlocks(training);

    removedBlockIdsRef.current.clear();
    removedExerciseIdsRef.current.clear();
    setFormValues(buildFormValues(training));
    setBlocks(nextBlocks);
    setTrainingSummary(buildEmptyTrainingSummary(nextBlocks));
    setIsSummaryLoading(false);
    setSummaryError('');
    setErrors({});
    setSelectedExercise(null);
    setActiveExerciseConfig(null);
    setSubmitError(isMissingTraining ? 'Treino nao encontrado.' : '');
  }, [isMissingTraining, training]);

  const hasActiveCatalogFilters = catalogSearchTerm.trim().length > 0 || catalogGroups.length > 0;
  const summarySignature = useMemo(() => buildSummarySignature(blocks, formValues.levels), [blocks, formValues.levels]);
  const summaryRequestPayload = useMemo<TrainingSummaryRequestPayload>(
    () => JSON.parse(summarySignature) as TrainingSummaryRequestPayload,
    [summarySignature],
  );
  const blockSummaryValuesById = useMemo(() => trainingSummary.blockSummaries.reduce<Record<string, SummaryMetricValues>>((accumulator, blockSummary) => {
    accumulator[blockSummary.blockId] = {
      totalVolume: blockSummary.totalVolume,
      totalTime: blockSummary.totalTime,
      loadPercentage: blockSummary.loadPercentage,
      loadIntensity: blockSummary.loadIntensity,
      effortPerception: blockSummary.effortPerception,
    };
    return accumulator;
  }, {}), [trainingSummary.blockSummaries]);
  const summaryMetrics = useMemo(() => buildSummaryMetrics({
    totalVolume: trainingSummary.totalVolume,
    totalTime: trainingSummary.totalTime,
    loadPercentage: trainingSummary.loadPercentage,
    loadIntensity: trainingSummary.loadIntensity,
    effortPerception: trainingSummary.effortPerception,
  }), [
    trainingSummary.effortPerception,
    trainingSummary.loadIntensity,
    trainingSummary.loadPercentage,
    trainingSummary.totalTime,
    trainingSummary.totalVolume,
  ]);
  const blockSummaryMetricsById = useMemo(() => blocks.reduce<Record<string, TrainingSummaryMetric[]>>((accumulator, block) => {
    const blockSummary = blockSummaryValuesById[block.id] ?? EMPTY_SUMMARY_VALUES;

    accumulator[block.id] = buildSummaryMetrics(blockSummary);

    return accumulator;
  }, {}), [blocks, blockSummaryValuesById]);

  useEffect(() => {
    if (summaryRequestPayload.blocks.every((block) => block.items.length === 0)) {
      setTrainingSummary(buildEmptyTrainingSummaryFromRequest(summaryRequestPayload));
      setIsSummaryLoading(false);
      setSummaryError('');
      return;
    }

    const requestId = summaryRequestIdRef.current + 1;
    summaryRequestIdRef.current = requestId;
    setIsSummaryLoading(true);
    setSummaryError('');

    const timeoutId = window.setTimeout(() => {
      void TrainingService.getTrainingSummary(summaryRequestPayload)
        .then((nextSummary) => {
          if (summaryRequestIdRef.current !== requestId) {
            return;
          }

          setTrainingSummary(nextSummary);
          setSummaryError('');
        })
        .catch(() => {
          if (summaryRequestIdRef.current !== requestId) {
            return;
          }

          setSummaryError('Nao foi possivel atualizar o resumo do treino.');
        })
        .finally(() => {
          if (summaryRequestIdRef.current === requestId) {
            setIsSummaryLoading(false);
          }
        });
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [summaryRequestPayload]);

  const handleFieldChange = useCallback((field: keyof TrainingFormValues, value: string | string[]) => {
    if (field === 'levels') {
      const nextLevels = expandLegacyWorkoutLevels(Array.isArray(value) ? value : [value]);

      if (nextLevels.length > MAX_WORKOUT_LEVELS) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          levels: WORKOUT_LEVELS_MAX_ERROR,
        }));
        return;
      }

      if (nextLevels.length > 0 && !areWorkoutLevelsContiguous(nextLevels)) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          levels: WORKOUT_LEVELS_GAP_ERROR,
        }));
        return;
      }

      setFormValues((currentValues) => ({
        ...currentValues,
        levels: nextLevels,
      }));
      setErrors((currentErrors) => ({
        ...currentErrors,
        levels: '',
      }));
      setSubmitError('');
      return;
    }

    const normalizedValue = field === 'duration' && typeof value === 'string'
      ? normalizePositiveIntegerValue(value)
      : value;

    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: normalizedValue,
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [field]: '',
    }));
    setSubmitError('');
  }, []);

  const handleCatalogSearchChange = useCallback((value: string) => {
    setCatalogFilter('catalogSearch', value);
  }, [setCatalogFilter]);

  const handleCatalogGroupsChange = useCallback((values: string[]) => {
    setCatalogFilter('catalogGroups', values);
  }, [setCatalogFilter]);

  const handleClearCatalogFilters = useCallback(() => {
    setCatalogFilters({ catalogSearch: '', catalogGroups: [] });
  }, [setCatalogFilters]);

  const handleSelectExercise = useCallback((exercise: ExerciseItem) => {
    setSelectedExercise(exercise);
    setSubmitError('');
  }, []);

  const hydrateBlockExerciseDefaults = useCallback(async (blockId: string, exerciseId: string, sourceExercise?: ExerciseItem) => {
    const fallbackExerciseId = findBlockExercise(blocks, blockId, exerciseId)?.exerciseId;
    const sourceExerciseId = sourceExercise?.id ?? fallbackExerciseId;

    if (!sourceExerciseId) {
      return;
    }

    try {
      const record = await ExerciseService.getExerciseById(sourceExerciseId);

      setBlocks((currentBlocks) => currentBlocks.map((block) => {
        if (block.id !== blockId) {
          return block;
        }

        const nextItems = updateExerciseTree(block.items, exerciseId, (item) => {
          return {
            ...item,
            execution: buildExecutionFromExerciseRecord(
              record,
              executionMethodOptions,
              item.execution,
              Boolean(item.hasExerciseDefaultsLoaded),
            ),
            hasExerciseDefaultsLoaded: true,
          };
        });

        return {
          ...block,
          items: syncExerciseGroupingTree(nextItems.nextItems),
        };
      }));
    } catch {
      if (sourceExercise) {
        setBlocks((currentBlocks) => currentBlocks.map((block) => {
          if (block.id !== blockId) {
            return block;
          }

          const nextItems = updateExerciseTree(block.items, exerciseId, (item) => ({
            ...item,
            execution: normalizeExecution({
              ...item.execution,
              ...buildExecutionSpeedFields({
                executionSpeed: item.execution.executionSpeed ?? sourceExercise.defaultExecutionSpeed,
                isometricSeconds: item.execution.isometricSeconds ?? sourceExercise.defaultExecutionSeconds,
                concentricSeconds: item.execution.concentricSeconds ?? sourceExercise.defaultConcentricSeconds,
                pauseSeconds: item.execution.pauseSeconds ?? sourceExercise.defaultPauseSeconds,
                eccentricSeconds: item.execution.eccentricSeconds ?? sourceExercise.defaultEccentricSeconds,
              }),
              executionSeconds: sourceExercise.defaultExecutionSeconds ?? item.execution.executionSeconds ?? null,
              exerciseType: sourceExercise.exerciseType ?? item.execution.exerciseType ?? null,
            }),
            hasExerciseDefaultsLoaded: true,
          }));

          return {
            ...block,
            items: syncExerciseGroupingTree(nextItems.nextItems),
          };
        }));
      }
    }
  }, [blocks, executionMethodOptions]);

  const handleOpenExerciseConfig = useCallback(async (blockId: string, exerciseId: string) => {
    const blockExercise = findBlockExercise(blocks, blockId, exerciseId);

    if (!blockExercise) {
      return;
    }

    await hydrateBlockExerciseDefaults(blockId, exerciseId);

    setActiveExerciseConfig({
      blockId,
      exerciseId,
    });
  }, [blocks, hydrateBlockExerciseDefaults]);

  const handleCloseExerciseConfig = useCallback(() => {
    setActiveExerciseConfig(null);
  }, []);

  const handleUpdateExerciseExecution = useCallback((blockId: string, exerciseId: string, execution: TrainingExerciseExecution) => {
    const exerciseIdsToHydrate = new Set<string>();

    setBlocks((currentBlocks) => currentBlocks.map((block) => {
      if (block.id !== blockId) {
        return block;
      }

      const nextItems = updateExerciseTree(block.items, exerciseId, (item) => {
        const normalizedExecution = normalizeExecution(execution);
        const currentGroupingType = resolveExerciseGroupingType(item, item.conjugatedExercises ?? [])
          ?? item.groupingType
          ?? 'multiple_set';
        const shouldResetTabataGroup = (item.conjugatedExercises?.length ?? 0) > 0
          && isTabataSupportedTrainingGroupingType(currentGroupingType)
          && isTabataTrainingExecutionMethod(item.execution.method)
          && !isTabataTrainingExecutionMethod(normalizedExecution.method);

        if (!shouldResetTabataGroup) {
          return {
            ...item,
            execution: normalizedExecution,
          };
        }

        const nextConjugatedExercises = resetExerciseTreeToUnconfiguredDefaults(item.conjugatedExercises ?? []);
        collectExerciseTreeIds(nextConjugatedExercises).forEach((id) => exerciseIdsToHydrate.add(id));

        return {
          ...item,
          execution: normalizedExecution,
          conjugatedExercises: nextConjugatedExercises,
        };
      });

      return {
        ...block,
        items: syncExerciseGroupingTree(nextItems.nextItems),
      };
    }));

    if (exerciseIdsToHydrate.size > 0) {
      void Promise.all(Array.from(exerciseIdsToHydrate).map((groupedExerciseId) => hydrateBlockExerciseDefaults(blockId, groupedExerciseId)))
        .catch(() => undefined);
    }
  }, [hydrateBlockExerciseDefaults]);

  const handleAddExerciseToBlock = useCallback((blockId: string) => {
    if (!selectedExercise) {
      setSubmitError('Selecione um exercicio no catalogo para adiciona-lo ao bloco.');
      return;
    }

    let nextBlockExerciseId = '';

    setBlocks((currentBlocks) => {
      const nextBlockExercise = createBlockExercise(selectedExercise);
      const nextState = insertExerciseIntoBlocks(currentBlocks, blockId, nextBlockExercise);
      nextBlockExerciseId = nextBlockExercise.id;
      return nextState.nextBlocks;
    });

    if (!nextBlockExerciseId) {
      return;
    }

    setActiveExerciseConfig({
      blockId,
      exerciseId: nextBlockExerciseId,
    });
    setErrors((currentErrors) => ({
      ...currentErrors,
      blocks: '',
    }));
    setSubmitError('');
    void hydrateBlockExerciseDefaults(blockId, nextBlockExerciseId, selectedExercise);
  }, [hydrateBlockExerciseDefaults, selectedExercise]);

  const handleDropCatalogExerciseToBlock = useCallback((blockId: string, exercise: ExerciseItem, targetExerciseId?: string, dropMode: ExerciseDropMode = 'insert') => {
    let nextBlockExerciseId = '';

    setBlocks((currentBlocks) => {
      const nextBlockExercise = createBlockExercise(exercise);
      const nextState = insertExerciseIntoBlocks(currentBlocks, blockId, nextBlockExercise, targetExerciseId, dropMode);
      nextBlockExerciseId = nextBlockExercise.id;
      return nextState.nextBlocks;
    });

    setSelectedExercise(exercise);

    if (nextBlockExerciseId) {
      setActiveExerciseConfig({
        blockId,
        exerciseId: nextBlockExerciseId,
      });
    }

    setErrors((currentErrors) => ({
      ...currentErrors,
      blocks: '',
    }));
    setSubmitError('');
    if (nextBlockExerciseId) {
      void hydrateBlockExerciseDefaults(blockId, nextBlockExerciseId, exercise);
    }
  }, [hydrateBlockExerciseDefaults]);

  const handleReplaceExercise = useCallback((
    blockId: string,
    itemId: string,
    nextExercise: ExerciseItem,
    replaceInEntirePlan: boolean,
  ) => {
    setBlocks((currentBlocks) => {
      const sourceItem = findBlockExercise(currentBlocks, blockId, itemId);

      if (!sourceItem) {
        return currentBlocks;
      }

      return syncBlocksGroupingState(replaceExerciseIdentityInBlocks(currentBlocks, {
        itemId,
        sourceCatalogId: sourceItem.exerciseId,
        nextExercise,
        replaceInEntirePlan,
      }));
    });
    setSubmitError('');
  }, []);

  const handleRemoveExerciseFromBlock = useCallback((blockId: string, exerciseId: string) => {
    setBlocks((currentBlocks) => currentBlocks.map((block) => {
      if (block.id !== blockId) {
        return block;
      }

      const removal = extractExerciseFromTree(block.items, exerciseId);
      const persistedExerciseId = removal.extractedExercise
        ? parsePersistedEntityId(removal.extractedExercise.id)
        : null;

      if (persistedExerciseId) {
        removedExerciseIdsRef.current.add(persistedExerciseId);
      }

      return {
        ...block,
          items: syncExerciseGroupingTree(removal.nextItems),
      };
    }));
    setActiveExerciseConfig((currentConfig) => {
      if (!currentConfig || currentConfig.exerciseId !== exerciseId) {
        return currentConfig;
      }

      return null;
    });
    setSubmitError('');
  }, []);

  const handleMoveExerciseLeft = useCallback((blockId: string, exerciseId: string) => {
    setBlocks((currentBlocks) => reorderBlockItems(currentBlocks, blockId, exerciseId, 'left'));
  }, []);

  const handleMoveExerciseRight = useCallback((blockId: string, exerciseId: string) => {
    setBlocks((currentBlocks) => reorderBlockItems(currentBlocks, blockId, exerciseId, 'right'));
  }, []);

  const handleMoveExerciseByDrag = useCallback((sourceBlockId: string, exerciseId: string, targetBlockId: string, targetExerciseId?: string, dropMode: ExerciseDropMode = 'insert') => {
    setBlocks((currentBlocks) => moveExerciseAcrossBlocks(
      currentBlocks,
      sourceBlockId,
      exerciseId,
      targetBlockId,
      targetExerciseId,
      dropMode,
    ));

    setActiveExerciseConfig((currentConfig) => {
      if (!currentConfig || currentConfig.exerciseId !== exerciseId) {
        return currentConfig;
      }

      return {
        ...currentConfig,
        blockId: targetBlockId,
      };
    });
    setSubmitError('');
  }, []);

  const handleMoveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    setBlocks((currentBlocks) => {
      const currentIndex = currentBlocks.findIndex((block) => block.id === blockId);

      if (currentIndex < 0) {
        return currentBlocks;
      }

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex < 0 || targetIndex >= currentBlocks.length) {
        return currentBlocks;
      }

      const targetBlock = currentBlocks[targetIndex];
      if (!targetBlock) {
        return currentBlocks;
      }

      return moveBlockByIds(currentBlocks, blockId, targetBlock.id, BLOCK_LABELS);
    });
    setSubmitError('');
  }, []);

  const handleMoveBlockByDrag = useCallback((sourceBlockId: string, targetBlockId: string) => {
    setBlocks((currentBlocks) => moveBlockByIds(currentBlocks, sourceBlockId, targetBlockId, BLOCK_LABELS));
    setSubmitError('');
  }, []);

  const handleMoveBlockToLabel = useCallback((blockId: string, targetLabel: string) => {
    setBlocks((currentBlocks) => moveBlockToLabel(currentBlocks, blockId, targetLabel, BLOCK_LABELS));
    setSubmitError('');
  }, []);

  const handleRemoveBlock = useCallback((blockId: string) => {
    setBlocks((currentBlocks) => {
      if (!currentBlocks.some((block) => block.id === blockId)) {
        return currentBlocks;
      }

      const persistedBlockId = parsePersistedEntityId(blockId);

      if (persistedBlockId) {
        removedBlockIdsRef.current.add(persistedBlockId);
      }

      if (currentBlocks.length <= 1) {
        return [createEmptyBlock(0, 'A')];
      }

      return relabelTrainingBlocks(
        currentBlocks.filter((block) => block.id !== blockId),
        BLOCK_LABELS,
      );
    });
    setActiveExerciseConfig((currentConfig) => {
      if (!currentConfig || currentConfig.blockId !== blockId) {
        return currentConfig;
      }

      return null;
    });
  }, []);

  const handleAddBlock = useCallback(() => {
    setBlocks((currentBlocks) => {
      if (currentBlocks.length >= BLOCK_LABELS.length) {
        setSubmitError(`Limite de blocos atingido (máximo: ${BLOCK_LABELS[MAX_BLOCK_LABEL_INDEX]})`);
        return currentBlocks;
      }
      setSubmitError('');
      return [...currentBlocks, createEmptyBlock(currentBlocks.length)];
    });
  }, []);

  const validateForm = useCallback(() => {
    const nextErrors: TrainingFormErrors = {};

    if (!formValues.name.trim()) {
      nextErrors.name = 'Informe o nome do treino.';
    }

    if (formValues.levels.length === 0) {
      nextErrors.levels = 'Selecione o nível.';
    } else if (formValues.levels.length > MAX_WORKOUT_LEVELS) {
      nextErrors.levels = WORKOUT_LEVELS_MAX_ERROR;
    } else if (!areWorkoutLevelsContiguous(formValues.levels)) {
      nextErrors.levels = WORKOUT_LEVELS_GAP_ERROR;
    }

    if (formValues.objective.length === 0) {
      nextErrors.objective = 'Selecione o objetivo.';
    }

    if (!formValues.month) {
      nextErrors.month = 'Selecione o mes.';
    }

    if (!formValues.description.trim()) {
      nextErrors.description = 'Descreva o treino.';
    }

    if (!formValues.location) {
      nextErrors.location = 'Selecione o local.';
    }

    if (!formValues.frequency) {
      nextErrors.frequency = 'Selecione a frequencia.';
    }

    if (!formValues.duration) {
      nextErrors.duration = 'Informe o tempo do treino.';
    } else if (!/^\d+$/.test(formValues.duration) || Number.parseInt(formValues.duration, 10) <= 0) {
      nextErrors.duration = 'Informe um tempo válido para o treino.';
    }

    if (!formValues.category) {
      nextErrors.category = 'Selecione a categoria.';
    }

    if (blocks.every((block) => block.items.length === 0)) {
      nextErrors.blocks = 'Adicione pelo menos um exercicio ao treino.';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }, [blocks, formValues]);

  const buildTrainingRecord = useCallback((): TrainingRecord => {
    const firstExercise = blocks.flatMap((block) => flattenBlockExercises(block.items))[0];
    const objectiveLabel = formatObjectiveLabels(formValues.objective);
    const categoryLabel = formValues.category ? TRAINING_CATEGORY_LABELS[formValues.category] : undefined;

    return {
      id: trainingId ?? '',
      code: training?.code ?? undefined,
      name: formValues.name.trim(),
      category: formValues.category || undefined,
      status: categoryLabel,
      splits: blocks.map((block) => block.label),
      loadIntensity: training?.loadIntensity ?? DEFAULT_SUMMARY_VALUE,
      effortPerception: training?.effortPerception ?? DEFAULT_SUMMARY_VALUE,
      totalVolume: training?.totalVolume ?? DEFAULT_SUMMARY_VALUE,
      objective: formValues.objective.length > 0 ? formValues.objective : undefined,
      descriptionLabel: objectiveLabel,
      description: formValues.description.trim(),
      group: firstExercise?.group ?? '',
      levels: formValues.levels,
      level: resolvePrimaryWorkoutLevel(formValues.levels) ?? '',
      month: formValues.month,
      monthIndex: Number.parseInt(formValues.month, 10) || undefined,
      location: formValues.location,
      frequency: formValues.frequency,
      sessionsPerWeek: Number.parseInt(formValues.frequency, 10) || undefined,
      duration: formValues.duration,
      durationMinutes: Number.parseInt(formValues.duration, 10) || undefined,
      isActive: true,
      updatedAt: training?.updatedAt,
      blocks,
      removedBlockIds: Array.from(removedBlockIdsRef.current),
      removedExerciseIds: Array.from(removedExerciseIdsRef.current),
    };
  }, [blocks, formValues, training?.code, training?.effortPerception, training?.loadIntensity, training?.totalVolume, training?.updatedAt, trainingId]);

  const handleSave = useCallback(async () => {
    if (isMissingTraining) {
      setSubmitError('Treino nao encontrado.');
      return null;
    }

    if (!validateForm()) {
      return null;
    }

    setIsSaving(true);

    try {
      const savedTraining = await storage.saveTraining(buildTrainingRecord());
      removedBlockIdsRef.current.clear();
      removedExerciseIdsRef.current.clear();
      setTraining(savedTraining);
      setSubmitError('');
      return savedTraining;
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Não foi possível salvar o treino. Tente novamente.',
      );
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [buildTrainingRecord, isMissingTraining, storage, validateForm]);

  return {
    mode: trainingId ? 'edit' : 'create',
    formValues,
    errors,
    blocks,
    submitError,
    isSaving,
    isLoadingTraining,
    isMissingTraining,
    catalogExercises,
    catalogLoading,
    catalogSearchTerm,
    catalogGroups,
    catalogGroupOptions,
    hasActiveCatalogFilters,
    selectedExercise,
    isSummaryLoading,
    summaryError,
    summaryMetrics,
    muscleVolumes: trainingSummary.muscleVolumes,
    blockSummaryMetricsById,
    activeExerciseConfig,
    executionMethodOptions,
    categoryOptions: CATEGORY_OPTIONS,
    levelOptions: LEVEL_OPTIONS,
    objectiveOptions: OBJECTIVE_OPTIONS,
    monthOptions: MONTH_OPTIONS,
    locationOptions: LOCATION_OPTIONS,
    frequencyOptions: FREQUENCY_OPTIONS,
    handleFieldChange,
    handleCatalogSearchChange,
    handleCatalogGroupsChange,
    handleClearCatalogFilters,
    handleSelectExercise,
    handleAddExerciseToBlock,
    handleDropCatalogExerciseToBlock,
    handleOpenExerciseConfig,
    handleCloseExerciseConfig,
    handleUpdateExerciseExecution,
    handleRemoveExerciseFromBlock,
    handleReplaceExercise,
    handleMoveExerciseLeft,
    handleMoveExerciseRight,
    handleMoveExerciseByDrag,
    handleMoveBlock,
    handleMoveBlockByDrag,
    handleMoveBlockToLabel,
    handleRemoveBlock,
    handleAddBlock,
    handleSave,
  };
};

export default useTrainingBuilder;
